/** Texts of the Illusion model (the 5M EMA pullback system with the 15M filter): its tuning, its summary, its chart, its checks. Each key in both languages. */
export const illusion = {
  en: {
    "iu.name": "Illusion",
    "iu.zone.long": "BUY ZONE {price} · EMA 10–21",
    "iu.zone.short": "SELL ZONE {price} · EMA 10–21",
    "iu.zone.level": "EMA 10–21 (war zone)",
    "iu.zone.active": "active zone",
    "iu.zone.next": "next zone",
    "iu.zone.none": "No zone – the 5M trend does not point this way.",
    "iu.tune": "Tuning",
    "iu.title": "Illusion tuning",
    "iu.about.title": "Illusion – the strategy",
    "iu.about.intro": "A 5-minute EMA pullback system after a forum template (“5Min Intraday System”, Forex Factory, rules of its 4th edition 2007) with the 15-minute chart as trend filter: trade only with a clear trend, enter when price pulls back to the averages, small stop, fixed target.",
    "iu.about.steps": "Lines on 5M: EMA 10 (orange), EMA 21 (blue), SMA 50 (grey).\nTrend: the template’s angle line — the change of the SMA 50 over 2 bars, smoothed by an EMA 21 — lies outside the no-trade zone (the template’s ±0.1, here ±0.09 ATR, measured on EUR/USD and GBP/USD 2006–07), and the EMA 10 lies on the trend’s side of the EMA 21. The template replaced its first rule, “50 SMA angle over 20°”, which it called subjective, by this line.\n15M filter (not in the template, your choice): on 15M the EMA 10 above the EMA 21 above the SMA 50 and the SMA 50 rising (mirrored for short).\nSetup: price pulls back through the EMA 10 towards the EMA 21 (the template’s “war zone”) — a limit order a little before the EMA 21, placed at the bar’s close, filling from the next bar on.\nStop and target: from the entry in ATR — the template’s 6 pips stop, 6 pips to break-even and 10–15 pips target, measured on EUR/USD and GBP/USD 5M 2006–07 as about 1 ATR, 1 ATR and 2 ATR; no trailing.\nSessions: only the first two setups of the London session (until New York opens) and of the New York session.\nOne trade at a time; when a bar reaches stop and target, the stop counts.\nNot built (subjective in the template): the small support/resistance line whose break cancels a setup, “smooth, not steep” pullbacks, the untested ADX rule.",
    "iu.about.evidence": "Measured on gold 5M 2021–2026 with the standard rules fixed beforehand and a spread of $0.30: 4,834 trades, before costs −0.02 R per trade (no edge), after costs −0.20 R per trade, negative in every year. The small stop (median $1.86) makes the spread cost 16 % of the risk. The template's own results are forum posts, not verified.",
    "iu.atr": "{n} ATR",
    "iu.off": "off",
    "iu.stop": "Stop",
    "iu.target": "Target",
    "iu.breakeven": "Stop to entry after",
    "iu.entry": "Limit before EMA 21",
    "iu.angle": "Angle line (no-trade zone)",
    "iu.angleAtr": "± {n} ATR",
    "iu.htf": "15M trend filter",
    "iu.sessions": "First 2 setups per session",
    "iu.preset.1": "Template",
    "iu.preset.2": "Without 15M filter",
    "iu.preset.3": "Without sessions",
    "iu.frames": "5M · filter 15M",
    "iu.long": "Long",
    "iu.short": "Short",
    "iu.trend.up": "up",
    "iu.trend.down": "down",
    "iu.trend.none": "no trend",
    "iu.trend.noBars": "no 15M prices",
    "iu.session.london": "London",
    "iu.session.newyork": "New York",
    "iu.session.in": "{name} · {used}/2 setups",
    "iu.session.outside": "outside London and New York",
    "iu.status": "Now",
    "iu.status.none": "no setup: trend, filter or session missing",
    "iu.status.wait": "waiting for the pullback to the EMA 10",
    "iu.status.trade": "{side} since {time}",
    "iu.status.order": "{side} limit armed",
    "iu.row.trend5": "Trend 5M",
    "iu.row.trend15": "Trend 15M",
    "iu.row.session": "Session",
    "iu.row.entry": "Entry",
    "iu.row.limit": "Limit",
    "iu.row.stop": "Stop",
    "iu.row.stopEven": "Stop (at entry)",
    "iu.row.target": "Target",
    "iu.chart.limit": "Limit {arrow}",
    "iu.chart.entry": "Entry {arrow}",
    "iu.chart.stop": "Stop",
    "iu.chart.stopEven": "Stop (entry)",
    "iu.chart.target": "Target",
    "iu.chart.breakeven": "Stop → entry",
    "iu.chart.setup": "Setup {arrow}",
    "iu.chart.setupHint": "Illusion: the limit before the EMA 21 filled after a pullback in the trend.",
    "iu.line.fast": "EMA 10",
    "iu.line.mid": "EMA 21",
    "iu.line.slow": "SMA 50",
    "chk.iuTrend5": "Trend 5M",
    "chk.iuTrend5.hint": "Illusion. ✓ buy while the angle line (EMA 21 of the SMA 50’s 2-bar change in ATR) lies above +0.09 ATR (the tuning) and the EMA 10 above the EMA 21; ✓ sell mirrored; in between the no-trade zone.",
    "chk.iuTrend15": "Trend 15M",
    "chk.iuTrend15.hint": "Illusion's filter. ✓ buy while on 15M the EMA 10 lies above the EMA 21 above the SMA 50 and the SMA 50 rises; ✓ sell mirrored. With the filter off both –.",
    "chk.iuSession": "Session with room",
    "chk.iuSession.hint": "Illusion. ✓ while the London session (until New York opens) or the New York session has had fewer than two setups; with the sessions off always ✓.",
    "chk.iuSetup": "Setup",
    "chk.iuSetup.hint": "Illusion. ✓ on its side while a limit is armed after a pullback through the EMA 10, or its trade runs.",
  },
  de: {
    "iu.name": "Illusion",
    "iu.zone.long": "BUY ZONE {price} · EMA 10–21",
    "iu.zone.short": "SELL ZONE {price} · EMA 10–21",
    "iu.zone.level": "EMA 10–21 (Kriegszone)",
    "iu.zone.active": "aktive Zone",
    "iu.zone.next": "nächste Zone",
    "iu.zone.none": "Keine Zone – der 5M-Trend zeigt nicht in diese Richtung.",
    "iu.tune": "Feintuning",
    "iu.title": "Illusion-Feintuning",
    "iu.about.title": "Illusion – die Strategie",
    "iu.about.intro": "Ein 5-Minuten-EMA-Rücklaufsystem nach einer Forum-Vorlage („5Min Intraday System“, Forex Factory, Regeln ihrer 4. Fassung 2007) mit dem 15-Minuten-Chart als Trendfilter: nur mit klarem Trend handeln, einsteigen, wenn der Kurs zu den Durchschnitten zurückläuft, kleiner Stop, festes Ziel.",
    "iu.about.steps": "Linien auf 5M: EMA 10 (orange), EMA 21 (blau), SMA 50 (grau).\nTrend: die Winkel-Linie der Vorlage — die Änderung des SMA 50 über 2 Kerzen, geglättet mit einem EMA 21 — liegt außerhalb der No-Trade-Zone (±0,1 der Vorlage, hier ±0,09 ATR, gemessen an EUR/USD und GBP/USD 2006–07), und der EMA 10 liegt auf der Trendseite des EMA 21. Die Vorlage hat ihre erste Regel „Winkel des SMA 50 über 20°“, die sie selbst subjektiv nennt, durch diese Linie ersetzt.\n15M-Filter (nicht aus der Vorlage, deine Wahl): auf 15M EMA 10 über EMA 21 über SMA 50 und der SMA 50 steigt (für Short gespiegelt).\nSetup: Der Kurs läuft durch den EMA 10 zum EMA 21 zurück (die „Kriegszone“ der Vorlage) — eine Limit-Order kurz vor dem EMA 21, zum Schluss der Kerze gesetzt, ausgeführt ab der nächsten Kerze.\nStop und Ziel: vom Einstieg in ATR — Stop 6 Pips, Einstand nach 6 Pips und Ziel 10–15 Pips der Vorlage, gemessen auf EUR/USD und GBP/USD 5M 2006–07 als etwa 1 ATR, 1 ATR und 2 ATR; kein Nachziehen.\nSitzungen: nur die ersten zwei Setups der London-Sitzung (bis New York öffnet) und der New-York-Sitzung.\nImmer nur ein Trade; erreicht eine Kerze Stop und Ziel, zählt der Stop.\nNicht gebaut (in der Vorlage subjektiv): die kleine Unterstützungs-/Widerstandslinie, deren Bruch ein Setup aufhebt, „glatte, nicht steile“ Rückläufe, die ungetestete ADX-Regel.",
    "iu.about.evidence": "Gemessen auf Gold 5M 2021–2026 mit den vorab festgelegten Standardregeln und 0,30 $ Spread: 4.834 Trades, vor Kosten −0,02 R je Trade (kein Vorteil), nach Kosten −0,20 R je Trade, in jedem Jahr negativ. Der kleine Stop (Median 1,86 $) lässt den Spread 16 % des Risikos kosten. Die Ergebnisse der Vorlage selbst sind Forenbeiträge, nicht geprüft.",
    "iu.atr": "{n} ATR",
    "iu.off": "aus",
    "iu.stop": "Stop",
    "iu.target": "Ziel",
    "iu.breakeven": "Stop auf Einstand nach",
    "iu.entry": "Limit vor EMA 21",
    "iu.angle": "Winkel-Linie (No-Trade-Zone)",
    "iu.angleAtr": "± {n} ATR",
    "iu.htf": "15M-Trendfilter",
    "iu.sessions": "Erste 2 Setups je Sitzung",
    "iu.preset.1": "Vorlage",
    "iu.preset.2": "Ohne 15M-Filter",
    "iu.preset.3": "Ohne Sitzungen",
    "iu.frames": "5M · Filter 15M",
    "iu.long": "Long",
    "iu.short": "Short",
    "iu.trend.up": "aufwärts",
    "iu.trend.down": "abwärts",
    "iu.trend.none": "kein Trend",
    "iu.trend.noBars": "keine 15M-Kurse",
    "iu.session.london": "London",
    "iu.session.newyork": "New York",
    "iu.session.in": "{name} · {used}/2 Setups",
    "iu.session.outside": "außerhalb London und New York",
    "iu.status": "Jetzt",
    "iu.status.none": "kein Setup: Trend, Filter oder Sitzung fehlt",
    "iu.status.wait": "wartet auf den Rücklauf zum EMA 10",
    "iu.status.trade": "{side} seit {time}",
    "iu.status.order": "{side}-Limit gesetzt",
    "iu.row.trend5": "Trend 5M",
    "iu.row.trend15": "Trend 15M",
    "iu.row.session": "Sitzung",
    "iu.row.entry": "Einstieg",
    "iu.row.limit": "Limit",
    "iu.row.stop": "Stop",
    "iu.row.stopEven": "Stop (auf Einstand)",
    "iu.row.target": "Ziel",
    "iu.chart.limit": "Limit {arrow}",
    "iu.chart.entry": "Einstieg {arrow}",
    "iu.chart.stop": "Stop",
    "iu.chart.stopEven": "Stop (Einstand)",
    "iu.chart.target": "Ziel",
    "iu.chart.breakeven": "Stop → Einstand",
    "iu.chart.setup": "Setup {arrow}",
    "iu.chart.setupHint": "Illusion: Das Limit vor dem EMA 21 wurde nach einem Rücklauf im Trend ausgeführt.",
    "iu.line.fast": "EMA 10",
    "iu.line.mid": "EMA 21",
    "iu.line.slow": "SMA 50",
    "chk.iuTrend5": "Trend 5M",
    "chk.iuTrend5.hint": "Illusion. ✓ Kauf, solange die Winkel-Linie (EMA 21 der Änderung des SMA 50 über 2 Kerzen in ATR) über +0,09 ATR liegt (Feintuning) und der EMA 10 über dem EMA 21; ✓ Verkauf gespiegelt; dazwischen die No-Trade-Zone.",
    "chk.iuTrend15": "Trend 15M",
    "chk.iuTrend15.hint": "Filter von Illusion. ✓ Kauf, solange auf 15M der EMA 10 über dem EMA 21 über dem SMA 50 liegt und der SMA 50 steigt; ✓ Verkauf gespiegelt. Mit Filter aus beide –.",
    "chk.iuSession": "Sitzung mit Platz",
    "chk.iuSession.hint": "Illusion. ✓, solange die London-Sitzung (bis New York öffnet) bzw. die New-York-Sitzung weniger als zwei Setups hatte; mit Sitzungen aus immer ✓.",
    "chk.iuSetup": "Setup",
    "chk.iuSetup.hint": "Illusion. ✓ auf seiner Seite, solange nach einem Rücklauf durch den EMA 10 ein Limit gesetzt ist oder sein Trade läuft.",
  },
};
